'use client';

import { FC, useState, useContext, useMemo } from 'react';
import type { FolderCreateDialogProps } from './FolderCreateDialog.types';
import { Alert, Button, ButtonGroup, Dialog, Flex, Input } from '@/_components';
import { UserContext } from '@/_contexts/UserProvider';
import { createFolderInS3 } from '@/_api/client-documents';
import { DocumentsContext } from '@/_contexts/DocumentsProvider';
import { ToastContext } from '@/_contexts/ToastProvider';
import { sanitizeS3Name, validateName } from '@/_utils/sanitize';

export const FolderCreateDialog: FC<FolderCreateDialogProps> = ({
  isOpen,
  onClose,
  companyId,
}) => {
  const { isAdvisor, token } = useContext(UserContext);
  const { onToggleFolderCreateDialog, addNewFolder, openDirectory } =
    useContext(DocumentsContext);
  const { addToast } = useContext(ToastContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [folder, setFolder] = useState<string>('');

  const validation = useMemo(() => validateName(folder), [folder]);

  const onSetFolder = (event: React.ChangeEvent<HTMLInputElement>) =>
    setFolder(event.target.value);

  const onCreateFolder = async () => {
    try {
      setLoading(true);
      await createFolderInS3({
        token,
        path: `${openDirectory}/${sanitizeS3Name(folder)}`,
        companyId: companyId!,
      });
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      addNewFolder(folder, openDirectory);
      setFolder('');
      setLoading(false);
      onToggleFolderCreateDialog();
    }
  };

  const onClear = () => {
    setFolder('');
    setLoading(false);
    return onClose();
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClear} title="Create Folder">
      <Dialog.Content>
        <Flex isColumn={true} gap={8}>
          {isAdvisor && (
            <Alert type="info">
              Add <b>STAFF ONLY</b> in the folder name if you want the folder to
              be hidden from the client&apos;s view.
            </Alert>
          )}
          <Input
            value={folder}
            onChange={onSetFolder}
            autoFocus={true}
            label="Folder"
            placeholder="Enter folder name here"
            helperText={!validation.isValid ? validation.error : undefined}
            helperTextColor={!validation.isValid ? 'critical' : undefined}
          />
          <ButtonGroup align="right">
            <Button intent="secondary" onClick={onClear} value="Cancel" />
            <Button
              intent="primary"
              isLoading={isLoading}
              onClick={onCreateFolder}
              value="Create"
              isDisabled={isLoading || !validation.isValid}
            />
          </ButtonGroup>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
};
